/**
 * Identificazione manuale di un titolo che la ricerca non ha risolto (F-076,
 * riprogettato dopo il caso International Seaways del 2026-08-25).
 *
 * ## Cosa non andava
 *
 * Un titolo quotato su venti venue produce venti righe OpenFIGI **con lo
 * stesso ticker**: cambia solo un codice di borsa Bloomberg che non dice
 * niente. Il dialog le mostrava tutte uguali, la scelta veniva inviata
 * indicando *il ticker* — e il server, cercando per ticker, prendeva sempre la
 * prima. Si cliccava su NYSE e si veniva rifiutati su una borsa mai guardata.
 * Peggio: diciotto di quelle venti righe erano comunque destinate a fallire,
 * senza che nulla lo dicesse prima.
 *
 * ## Come funziona ora
 *
 * Ogni candidato è **descritto con l'esito che avrebbe** — simbolo di
 * quotazione, mercato, valuta — e la selezione viaggia per **indice**, che è
 * l'unica chiave davvero univoca. Le righe utilizzabili stanno in cima; quelle
 * che non lo sono restano consultabili ma raccolte, con il motivo.
 */

import { useState, type FormEvent } from 'react';
import { Alert, Button, Field, Modal, Spinner, TableWrap } from '@/ui';
import { ApiError } from '@/lib/api-client';
import { BrokerTickerField } from '@/features/connectors/BrokerTickerField';
import { formatExchangeCode } from '@/features/watchlist/openfigi-exchange-codes';
import { useReconcileResult, useResultCandidates, type DescribedCandidate, type DiscoveryResult } from './api';

type Props = { runId: string; result: DiscoveryResult; onClose: () => void };

export function DiscoveryReconcileModal({ runId, result, onClose }: Props) {
  const candidates = useResultCandidates(runId, result.id);
  const reconcile = useReconcileResult(runId);
  const [rowCurrency, setRowCurrency] = useState<Record<number, string>>({});
  const [manualTicker, setManualTicker] = useState('');
  const [manualCurrency, setManualCurrency] = useState('');
  const [showRejected, setShowRejected] = useState(false);
  const error = reconcile.error instanceof ApiError ? reconcile.error : null;
  const resolvedInstrumentId = reconcile.data?.instrumentId ?? null;

  const items = candidates.data?.items ?? [];
  const usable = items.filter((candidate) => candidate.usable);
  const rejected = items.filter((candidate) => !candidate.usable);

  const choose = (candidate: DescribedCandidate) => {
    const currency = candidate.resolvedCurrency ?? rowCurrency[candidate.index]?.trim();
    reconcile.mutate({
      resultId: result.id,
      // L'indice, non il ticker: venti candidati lo condividono.
      candidateIndex: candidate.index,
      ...(currency ? { currency } : {}),
    });
  };

  const submitManual = (event: FormEvent) => {
    event.preventDefault();
    reconcile.mutate({
      resultId: result.id,
      ticker: manualTicker.trim(),
      ...(manualCurrency.trim() ? { currency: manualCurrency.trim() } : {}),
    });
  };

  if (resolvedInstrumentId) {
    return (
      <Modal title={`Identifica ${result.displayName}`} onClose={onClose}>
        <Alert variant="info" title="Titolo identificato">
          Ora è collegato al catalogo: puoi aprirne il dettaglio, aggiungerlo alla watchlist, ed è già
          disponibile per le prossime ricerche.
        </Alert>
        <div className="discovery-reconcile__broker">
          <h4>Ticker del broker (facoltativo)</h4>
          <p className="cell--minor">Serve solo per operare su Trading 212. Puoi farlo anche più tardi.</p>
          <BrokerTickerField
            instrumentId={resolvedInstrumentId}
            instrumentName={result.displayName}
            currentTicker={null}
            defaultQuery={result.displayName}
          />
        </div>
        <div className="discovery-reconcile__actions">
          <Button onClick={onClose}>Chiudi</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Identifica ${result.displayName}`} onClose={onClose}>
      {error && (
        <Alert variant="error" title="Identificazione non riuscita">
          {error.message}
        </Alert>
      )}

      <p className="discovery-reconcile__intro">
        La ricerca ha trovato questo nome ma non è riuscita a collegarlo con certezza a un titolo
        quotato. Identificandolo lo rendi utilizzabile — <strong>senza</strong> aggiungerlo alla
        watchlist — e resterà a catalogo per le ricerche future.
      </p>

      {candidates.isLoading && <Spinner label="Carico i candidati…" />}

      {usable.length > 0 && (
        <>
          <h4 className="discovery-reconcile__heading">
            {usable.length} {usable.length === 1 ? 'quotazione utilizzabile' : 'quotazioni utilizzabili'}
          </h4>
          <p className="cell--minor">
            Per ciascuna è indicato il simbolo che verrà usato per le quotazioni: scegli quella sulla
            borsa che ti interessa.
          </p>
          <CandidateTable
            candidates={usable}
            rowCurrency={rowCurrency}
            onCurrencyChange={setRowCurrency}
            onChoose={choose}
            pending={reconcile.isPending}
          />
        </>
      )}

      {candidates.data && usable.length === 0 && (
        <Alert variant="warning" title="Nessun candidato utilizzabile">
          OpenFIGI conosce questo titolo, ma solo su borse per cui non abbiamo un simbolo di quotazione
          verificato. Inserisci il simbolo completo qui sotto.
        </Alert>
      )}

      {rejected.length > 0 && (
        <details
          className="discovery-reconcile__rejected"
          open={showRejected}
          onToggle={(event) => setShowRejected((event.target as HTMLDetailsElement).open)}
        >
          <summary>
            Altre {rejected.length} quotazioni trovate, su borse che non sappiamo interrogare
          </summary>
          <p className="cell--minor">
            Le mostriamo perché dicono dov&apos;altro è quotato il titolo, ma sceglierle non porterebbe a
            un prezzo: per quelle borse non abbiamo un suffisso verificato.
          </p>
          <TableWrap tall>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Borsa</th>
                  <th>Tipo</th>
                  <th>Valuta</th>
                </tr>
              </thead>
              <tbody>
                {rejected.map((candidate) => (
                  <tr key={candidate.index} className="discovery-reconcile__row--off">
                    <td>{candidate.securityDescription ?? candidate.ticker}</td>
                    <td>{formatExchangeCode(candidate.exchange)}</td>
                    <td>{candidate.securityType ?? '—'}</td>
                    <td>{candidate.currency ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </details>
      )}

      <form onSubmit={submitManual} className="discovery-reconcile__manual">
        <h4 className="discovery-reconcile__heading">Oppure inserisci il simbolo</h4>
        <p className="cell--minor">
          Il simbolo di quotazione come lo conosci, suffisso compreso: <code>ABBN.SW</code>,{' '}
          <code>LDO.MI</code>, <code>AAPL</code>.
        </p>
        <div className="discovery-reconcile__manual-row">
          <Field
            label="Simbolo"
            value={manualTicker}
            onChange={(event) => setManualTicker(event.target.value.toUpperCase())}
            placeholder="ABBN.SW"
            maxLength={40}
          />
          <Field
            label="Valuta"
            value={manualCurrency}
            onChange={(event) => setManualCurrency(event.target.value.toUpperCase())}
            placeholder="CHF"
            maxLength={3}
            hint="Solo se non la ricaviamo"
          />
        </div>
        <Button type="submit" disabled={reconcile.isPending || manualTicker.trim().length === 0}>
          {reconcile.isPending ? 'Identifico…' : 'Identifica'}
        </Button>
      </form>
    </Modal>
  );
}

/**
 * La tabella dei candidati utilizzabili. Mostra **l'esito** e non solo i dati
 * grezzi: il simbolo che verrà usato e il mercato sono ciò che permette di
 * riconoscere la riga giusta fra venti che hanno lo stesso ticker.
 */
function CandidateTable({
  candidates,
  rowCurrency,
  onCurrencyChange,
  onChoose,
  pending,
}: {
  candidates: DescribedCandidate[];
  rowCurrency: Record<number, string>;
  onCurrencyChange: (updater: (current: Record<number, string>) => Record<number, string>) => void;
  onChoose: (candidate: DescribedCandidate) => void;
  pending: boolean;
}) {
  return (
    <TableWrap tall>
      <table className="table table--compact">
        <thead>
          <tr>
            <th>Quotazione</th>
            <th>Mercato</th>
            <th>Tipo</th>
            <th>Valuta</th>
            <th aria-label="Azioni" />
          </tr>
        </thead>
        <tbody>
          {candidates.map((candidate) => (
            <tr key={candidate.index}>
              <td>
                <strong>{candidate.providerSymbol}</strong>
                {candidate.securityDescription && candidate.securityDescription !== candidate.ticker && (
                  <span className="cell--minor"> · {candidate.securityDescription}</span>
                )}
              </td>
              <td>
                {candidate.marketName ?? formatExchangeCode(candidate.exchange)}
                {candidate.mic && <span className="cell--minor"> · {candidate.mic}</span>}
              </td>
              <td>{candidate.securityType ?? '—'}</td>
              <td>
                {candidate.resolvedCurrency ?? (
                  <input
                    className="discovery-reconcile__currency"
                    aria-label={`Valuta per ${candidate.providerSymbol}`}
                    placeholder="USD"
                    maxLength={3}
                    value={rowCurrency[candidate.index] ?? ''}
                    onChange={(event) =>
                      onCurrencyChange((current) => ({
                        ...current,
                        [candidate.index]: event.target.value.toUpperCase(),
                      }))
                    }
                  />
                )}
              </td>
              <td>
                <Button variant="ghost" disabled={pending} onClick={() => onChoose(candidate)}>
                  Scegli
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}
